import { Card, Col, Row, Select, Spin } from "antd";
import StatsAtGlanceCard from "@/components/UI/Cards/standardCard";
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import {
  useGetDailyStatsQuery,
  useGetPurchaseStatQuery,
} from "@/services/api/dashboard/dashboardApi";
import { formatCurrency, formatNumber } from "@/utils/helper";

const { Option } = Select;

// Custom tooltip for line chart
const CustomLineTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-black p-2 rounded shadow-lg">
        <p className="font-medium text-white mb-2">{`${label}`}</p>
        {payload.map((entry, index) => (
          <div className="flex gap-2" key={index}>
            <div
              className="p-1 h-4 w-4 rounded "
              style={{ backgroundColor: entry.color }}
            ></div>
            <p className="text-sm text-white">
              {`${entry.name}: ${formatNumber(entry.value)} orders`}
            </p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const StatGlance = () => {
  //getting purchase stats for top three cards
  const {
    data: purchaseStats,
    // isFetching: purchaseStatsFetching,
  } = useGetPurchaseStatQuery();
  //getting combined purchase data for line chart
  const {
    data: comebinedPurchase,
    isLoading: dailyStatsLoading,
    isError: dailyStatsError,
    error: dailyError,
    refetch: refetchDailyStats,
  } = useGetDailyStatsQuery();

  // console.log("Raw API Response:", comebinedPurchase);

  const transformLineChartData = (apiData) => {
    const bulkPurchaseEntries = Object.entries(apiData.bulk_purchase);
    // .slice(
    //   0,
    //   limit
    // );

    return bulkPurchaseEntries.map(([date, bulkValue]) => ({
      month: date,
      bulkPurchase: bulkValue,
      eventCatering: apiData.event_catering[date] || 0,
    }));
  };
  // Usage
  const lineChartData = comebinedPurchase
    ? transformLineChartData(comebinedPurchase)
    : [];

  return (
    <Card
      title="Stats at a glance"
      className="shadow-sm border border-gray-100"
      extra={
        <Select defaultValue="month" size="small" className="w-20">
          {/* <Option value="today">Today</Option>
          <Option value="week">Week</Option> */}
          <Option value="month">Month</Option>
        </Select>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <StatsAtGlanceCard
            title="BULK PURCHASE"
            value={formatCurrency(purchaseStats?.bulk_purchase) || "0"}
            icon={<ShoppingCartOutlined />}
            color="gray"
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <StatsAtGlanceCard
            title="EVENT CATERING"
            value={formatCurrency(purchaseStats?.event_catering) || "0"}
            icon={<CalendarOutlined />}
            color="gray"
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <StatsAtGlanceCard
            title="FOOD GIFTING"
            value={formatCurrency(purchaseStats?.gift_combined) || "0"}
            icon={<UserOutlined />}
            color="gray"
          />
        </Col>
      </Row>

      {/* Line chart section */}
      <Row className="mt-8">
        <Col xs={24}>
          <div className="h-80 mt-6">
            {dailyStatsLoading ? (
              <div className="flex items-center justify-center h-full">
                <Spin size="large" />
              </div>
            ) : dailyStatsError ? (
              <div className="flex flex-col items-center justify-center h-full gap-2">
                <div className="text-red-500 text-sm">
                  {dailyError?.data?.message || "Failed to load chart data"}
                </div>
                <button
                  onClick={refetchDailyStats}
                  className="text-xs text-blue-600 hover:text-blue-700 underline"
                >
                  Retry
                </button>
              </div>
            ) : lineChartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={lineChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />

                  <Tooltip content={<CustomLineTooltip />} />

                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    wrapperStyle={{
                      paddingBottom: "20px",
                      fontSize: "14px",
                    }}
                  />

                  <Line
                    type="monotone"
                    dataKey="bulkPurchase"
                    name="Bulk Purchase"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{
                      r: 4,
                      stroke: "#F59E0B",
                      strokeWidth: 2,
                      fill: "#F59E0B",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="eventCatering"
                    name="Event Catering"
                    stroke="#06B6D4"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={false}
                    activeDot={{
                      r: 4,
                      stroke: "#06B6D4",
                      strokeWidth: 2,
                      fill: "#06B6D4",
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default StatGlance;
